import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { findSeoRoute, noindexPaths, pageUrl, organization, OG_IMAGE } from '../data/seo';
import { buildStructuredData } from '../utils/structuredData';
import { trackLanding, trackEvent } from '../utils/analytics';
import { loadGtm } from '../utils/gtm';

/* ───────── head helpers ───────── */
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function removeAlternates() {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(n => n.remove());
}
function setJsonLd(data: object | null) {
  const id = 'bciti-jsonld';
  let el = document.getElementById(id);
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.setAttribute('type', 'application/ld+json');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Owns the document <head> for every route: title, description, canonical,
 * hreflang, Open Graph / Twitter, robots, html lang and JSON-LD.
 * Also keeps the `?lang=en` URL parameter in sync with the FR | EN selector so
 * each language version has its own crawlable, shareable URL.
 */
export function Seo() {
  const {
    lang,
    setLang
  } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // One landing-attribution event per session (organic / AI referrers / UTM).
  useEffect(() => {
    loadGtm();
    trackLanding();
  }, []);

  // URL → language (back/forward, shared links).
  useEffect(() => {
    const param = new URLSearchParams(location.search).get('lang');
    if (param === 'en' && lang !== 'EN') setLang('EN');
    if (param === 'fr' && lang !== 'FR') setLang('FR');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Language → URL (FR = no parameter, EN = ?lang=en).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get('lang');
    const wanted = lang === 'EN' ? 'en' : null;
    if (current === wanted || current === 'fr' && wanted === null) return;
    if (wanted) params.set('lang', wanted);else params.delete('lang');
    const search = params.toString();
    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
      hash: location.hash
    }, {
      replace: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, location.pathname, location.search]);

  // Head tags.
  useEffect(() => {
    const route = findSeoRoute(location.pathname);
    const isNoindex = !route || noindexPaths.includes(location.pathname);
    const locale = lang === 'FR' ? 'fr_CA' : 'en_CA';
    document.documentElement.lang = lang === 'FR' ? 'fr-CA' : 'en-CA';
    setMeta('name', 'robots', isNoindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');
    if (!route) {
      document.title = organization.name;
      removeAlternates();
      setJsonLd(null);
      return;
    }
    const title = lang === 'FR' ? route.title.fr : route.title.en;
    const description = lang === 'FR' ? route.description.fr : route.description.en;
    const url = pageUrl(route.path, lang);
    document.title = title;
    setMeta('name', 'description', description);
    setLink('canonical', url);
    removeAlternates();
    setLink('alternate', pageUrl(route.path, 'FR'), 'fr-CA');
    setLink('alternate', pageUrl(route.path, 'EN'), 'en-CA');
    setLink('alternate', pageUrl(route.path, 'FR'), 'x-default');
    setMeta('property', 'og:type', route.kind === 'caseStudy' ? 'article' : 'website');
    setMeta('property', 'og:site_name', organization.name);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:locale', locale);
    setMeta('property', 'og:locale:alternate', lang === 'FR' ? 'en_CA' : 'fr_CA');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', OG_IMAGE);
    setJsonLd(buildStructuredData(route, lang));
  }, [location.pathname, lang]);

  // Virtual page views for Tag Manager / GA4 (single-page app).
  useEffect(() => {
    trackEvent('page_view', {
      page_location: window.location.href,
      page_title: document.title
    });
  }, [location.pathname, lang]);
  return null;
}